import {
  Country,
  GameMode,
  GameState,
  HistoryItem,
  MultiQuestionEntry,
  QuestionInstance,
  SubmissionResult,
} from '../../types';
import { APP_CONFIG } from '../../constants';
import { getGameData } from '../dataLoader';
import { checkAnswer } from '../templatesHelper';
import {
  buildFallbackQuestion,
  buildRecentQuestionKeys,
  generateQuestion,
  getQuestionKey,
  getRemainingCountryCount,
} from '../questionGenerator';

export const MULTI_QUESTION_CONFIG = {
  questionCount: 5,
  answersPerQuestion: 5,
  minPossibleAnswers: 5,
  minAvailableAnswers: 5,
  maxPossibleAnswers: 40,
  maxLives: 5,
};

const buildMultiQuestionKeys = (questions: MultiQuestionEntry[]): Set<string> => {
  const keys = new Set<string>();
  questions.forEach((entry) => {
    keys.add(getQuestionKey(entry.question.templateId, entry.question.subjectIso3));
  });
  return keys;
};

const buildQuestionHistoryItem = (
  question: QuestionInstance,
  countryIso3: string,
  isCorrect: boolean
): HistoryItem => ({
  questionText: question.text,
  templateId: question.templateId,
  subjectIso3: question.subjectIso3,
  answerIso3: countryIso3,
  isCorrect,
  timestamp: Date.now(),
});

const buildMultiQuestions = (
  usedIsoCodes: Set<string>,
  burnedIsoCodes: Set<string>,
  history: HistoryItem[],
  seedCount = 0,
  mode: GameMode
): MultiQuestionEntry[] => {
  const questions: MultiQuestionEntry[] = [];
  const recentKeys = buildRecentQuestionKeys(history, APP_CONFIG.QUESTION_COOLDOWN_COUNT);
  let lastCategory: QuestionInstance['category'] | null = null;

  for (let index = 0; index < MULTI_QUESTION_CONFIG.questionCount; index += 1) {
    const question = generateQuestion(
      seedCount + index,
      lastCategory,
      usedIsoCodes,
      burnedIsoCodes,
      recentKeys,
      history,
      {
        minPossibleAnswers: MULTI_QUESTION_CONFIG.minPossibleAnswers,
        minAvailableAnswers: MULTI_QUESTION_CONFIG.minAvailableAnswers,
        maxPossibleAnswers: MULTI_QUESTION_CONFIG.maxPossibleAnswers,
      },
      { mode }
    ) ?? buildFallbackQuestion(
      seedCount + index,
      {
        minPossibleAnswers: MULTI_QUESTION_CONFIG.minPossibleAnswers,
        minAvailableAnswers: MULTI_QUESTION_CONFIG.minAvailableAnswers,
        maxPossibleAnswers: MULTI_QUESTION_CONFIG.maxPossibleAnswers,
      },
      usedIsoCodes,
      burnedIsoCodes,
      recentKeys,
      { mode },
      history
    );

    questions.push({ question, answers: [] });
    recentKeys.add(getQuestionKey(question.templateId, question.subjectIso3));
    lastCategory = question.category;
  }

  return questions;
};

export const buildMultiQuestionSet = (mode: GameMode): GameState => {
  const questions = buildMultiQuestions(new Set(), new Set(), [], 0, mode);
  const initialScore =
    mode === GameMode.FIVE_FOR_FIVE
      ? MULTI_QUESTION_CONFIG.questionCount * MULTI_QUESTION_CONFIG.answersPerQuestion
      : 0;

  return {
    mode,
    status: 'PLAYING',
    score: initialScore,
    lives: MULTI_QUESTION_CONFIG.maxLives,
    maxLives: MULTI_QUESTION_CONFIG.maxLives,
    currentQuestion: questions[0].question,
    history: [],
    usedIsoCodes: new Set(),
    burnedIsoCodes: new Set(),
    multiQuestions: questions,
    completedQuestionCount: 0,
    recoveryTokens: 0,
    startTime: Date.now(),
  };
};

const findMatchingQuestionIndex = (
  questions: MultiQuestionEntry[],
  country: Country
): number => {
  const data = getGameData();
  return questions.findIndex((entry) =>
    entry.answers.length < MULTI_QUESTION_CONFIG.answersPerQuestion &&
    checkAnswer(entry.question.templateId, entry.question.subjectIso3, country, data)
  );
};

const findMatchingQuestionIndexes = (
  questions: MultiQuestionEntry[],
  country: Country
): number[] => {
  const data = getGameData();
  return questions.reduce<number[]>((matches, entry, index) => {
    if (
      entry.answers.length < MULTI_QUESTION_CONFIG.answersPerQuestion &&
      checkAnswer(entry.question.templateId, entry.question.subjectIso3, country, data)
    ) {
      matches.push(index);
    }
    return matches;
  }, []);
};

const getQuestionForReplacement = (
  seed: number,
  usedIsoCodes: Set<string>,
  burnedIsoCodes: Set<string>,
  history: HistoryItem[],
  activeQuestions: MultiQuestionEntry[],
  lastCategory: QuestionInstance['category'] | null,
  mode: GameMode
): QuestionInstance | null => {
  const recentKeys = buildRecentQuestionKeys(history, APP_CONFIG.QUESTION_COOLDOWN_COUNT);
  const activeKeys = buildMultiQuestionKeys(activeQuestions);
  activeKeys.forEach((key) => recentKeys.add(key));

  return generateQuestion(
    seed,
    lastCategory,
    usedIsoCodes,
    burnedIsoCodes,
    recentKeys,
    history,
    {
      minPossibleAnswers: MULTI_QUESTION_CONFIG.minPossibleAnswers,
      minAvailableAnswers: MULTI_QUESTION_CONFIG.minAvailableAnswers,
      maxPossibleAnswers: MULTI_QUESTION_CONFIG.maxPossibleAnswers,
    },
    { mode }
  );
};

const hasRemainingCountries = (usedIsoCodes: Set<string>, burnedIsoCodes: Set<string>): boolean => {
  const data = getGameData();
  return getRemainingCountryCount(data, usedIsoCodes, burnedIsoCodes) > 0;
};

export const submitMultiQuestionAnswer = (
  state: GameState,
  country: Country
): SubmissionResult => {
  const questions = state.multiQuestions ?? [];
  const usedIsoCodes = new Set(state.usedIsoCodes);
  const burnedIsoCodes = new Set(state.burnedIsoCodes);
  const isGridGauntlet = state.mode === GameMode.GRID_GAUNTLET;
  const allowsMultiMatch =
    isGridGauntlet || state.mode === GameMode.FIVE_FOR_FIVE;
  const matchesIndex = findMatchingQuestionIndex(questions, country);
  const matchesIndexes = allowsMultiMatch
    ? findMatchingQuestionIndexes(questions, country)
    : matchesIndex !== -1
    ? [matchesIndex]
    : [];
  const isRolling = state.mode === GameMode.ROLLING_FIVE;
  const recoveryTokens = state.recoveryTokens ?? 0;
  const alreadyUsed = usedIsoCodes.has(country.iso3);
  const canRecover = isRolling && recoveryTokens > 0;

  if (questions.length === 0) {
    return {
      valid: false,
      message: 'No active questions available.',
      newState: state,
    };
  }

  if (alreadyUsed && !canRecover) {
    return {
      valid: false,
      message: `${country.name} has already been used!`,
      newState: state,
    };
  }

  if (burnedIsoCodes.has(country.iso3)) {
    return {
      valid: false,
      message: `${country.name} was lost previously!`,
      newState: state,
    };
  }

  if (matchesIndexes.length > 0) {
    const newQuestions = questions.map((entry, index) => {
      if (!matchesIndexes.includes(index)) return entry;
      return {
        ...entry,
        answers: [...entry.answers, country.iso3],
      };
    });

    if (!alreadyUsed) {
      usedIsoCodes.add(country.iso3);
    }

    const updatedRecoveryTokens = alreadyUsed && canRecover ? recoveryTokens - 1 : recoveryTokens;
    const newHistoryItems = matchesIndexes.map((index) =>
      buildQuestionHistoryItem(questions[index].question, country.iso3, true)
    );
    const updatedHistory = [...newHistoryItems, ...state.history];

    if (state.mode === GameMode.FIVE_FOR_FIVE) {
      const allCompleted = newQuestions.every(
        (entry) => entry.answers.length >= MULTI_QUESTION_CONFIG.answersPerQuestion
      );
      const status = allCompleted ? 'GAME_OVER' : 'PLAYING';

      return {
        valid: true,
        message: 'Correct!',
        newState: {
          ...state,
          status,
          endTime: allCompleted ? Date.now() : state.endTime,
          history: updatedHistory,
          usedIsoCodes,
          multiQuestions: newQuestions,
          currentQuestion: newQuestions[0]?.question ?? state.currentQuestion,
          recoveryTokens: updatedRecoveryTokens,
          score: Math.max(state.score - matchesIndexes.length, 0),
        },
      };
    }

    let completedQuestionCount = state.completedQuestionCount ?? 0;
    let nextQuestions = newQuestions;
    let status: GameState['status'] = 'PLAYING';
    let nextRecoveryTokens = updatedRecoveryTokens;
    const completedIndexes = matchesIndexes.filter(
      (index) => newQuestions[index].answers.length >= MULTI_QUESTION_CONFIG.answersPerQuestion
    );

    if (isGridGauntlet && completedIndexes.length > 0) {
      const sortedCompletedIndexes = [...completedIndexes].sort((a, b) => a - b);
      let remainingQuestions = nextQuestions.filter((_, index) => !completedIndexes.includes(index));

      sortedCompletedIndexes.forEach((index) => {
        const completedQuestion = nextQuestions[index];
        completedQuestionCount += 1;

        const replacement = getQuestionForReplacement(
          completedQuestionCount,
          usedIsoCodes,
          burnedIsoCodes,
          updatedHistory,
          remainingQuestions,
          completedQuestion.question.category,
          state.mode
        );

        if (!replacement) {
          status = 'GAME_OVER';
        } else {
          remainingQuestions = [...remainingQuestions, { question: replacement, answers: [] }];
        }
      });

      nextQuestions = remainingQuestions;
    } else {
      completedIndexes.forEach((index) => {
        const completedQuestion = nextQuestions[index];
        completedQuestionCount += 1;
        if (isRolling) {
          nextRecoveryTokens += 1;
        }

        const replacement = getQuestionForReplacement(
          completedQuestionCount,
          usedIsoCodes,
          burnedIsoCodes,
          updatedHistory,
          nextQuestions,
          completedQuestion.question.category,
          state.mode
        );

        if (!replacement) {
          status = 'GAME_OVER';
        } else {
          nextQuestions = nextQuestions.map((entry, entryIndex) =>
            entryIndex === index ? { question: replacement, answers: [] } : entry
          );
        }
      });
    }

    const matchedCount = matchesIndexes.length;
    const extraLifeEarned = isGridGauntlet && matchedCount >= 3 && state.lives < state.maxLives;
    const updatedLives = extraLifeEarned ? state.lives + 1 : state.lives;

    if (
      status === 'PLAYING' &&
      !hasRemainingCountries(usedIsoCodes, burnedIsoCodes) &&
      nextRecoveryTokens === 0
    ) {
      status = 'GAME_OVER';
    }

    return {
      valid: true,
      message: extraLifeEarned
        ? 'Correct! +1 life'
        : matchedCount > 1
        ? `Correct! ${matchedCount} questions`
        : alreadyUsed
        ? 'Recovered!'
        : 'Correct!',
      newState: {
        ...state,
        status,
        endTime: status === 'GAME_OVER' ? Date.now() : state.endTime,
        history: updatedHistory,
        usedIsoCodes,
        multiQuestions: nextQuestions,
        completedQuestionCount,
        recoveryTokens: nextRecoveryTokens,
        score: completedQuestionCount,
        currentQuestion: nextQuestions[0]?.question ?? state.currentQuestion,
        lives: updatedLives,
      },
    };
  }

  const newLives = state.lives - 1;
  let isGameOver = newLives <= 0;
  const newHistory = buildQuestionHistoryItem(
    questions[0]?.question ?? state.currentQuestion,
    country.iso3,
    false
  );
  const updatedHistory = [newHistory, ...state.history];

  if (!alreadyUsed) {
    burnedIsoCodes.add(country.iso3);
  }

  if (!isGameOver && !hasRemainingCountries(usedIsoCodes, burnedIsoCodes) && recoveryTokens === 0) {
    isGameOver = true;
  }

  return {
    valid: false,
    message: 'Incorrect!',
    burnedCountry: country,
    newState: {
      ...state,
      status: isGameOver ? 'GAME_OVER' : 'PLAYING',
      endTime: isGameOver ? Date.now() : state.endTime,
      lives: newLives,
      score: state.score,
      history: updatedHistory,
      burnedIsoCodes,
    },
  };
};
